import { isEqual } from 'lodash'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import PartnerList from '../partner/page'
import { PricingTierSettings } from '../pricing-tier/columns'
import RoleList from '../role/page'
import UserList from '../user/page'

import { PricingTierComponents } from '@/api'
import { IOrganization, getGrantsForPricingTier } from '@/common'
import OrganizationDataPoints from '@/components/organization-datapoints'
import OrganizationPartnership from '@/components/organization-partnership'
import OrganizationSettings from '@/components/organization-settings'
import PricingTierAdvancedSettings from '@/components/pricing-tier-settings'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MultiSelectAutoComplete } from '@/components/ui/multi-select-autocomplete'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { toast } from '@/components/ui/use-toast'
import { useAuth, useDataPoints, usePricingTiers, useTags } from '@/providers'
import {
  useMutationAssignPricingTier,
  useMutationUpdateOrganization,
  useMutationUpdatePricingTier,
  useMutationUpdateRole,
  useQueryGetActivePartnerByOrgId,
  useQueryGetAllRoles,
  useQueryGetOrganization,
  useQueryGetOrganizationSettings,
  useQueryGetPricingTierByOrganizationId,
  useQueryUsersByOrgId
} from '@/services'

const OrganizationDetail = () => {
  const { orgId } = useParams<{
    orgId: string
  }>()

  const { data: organization, isLoading: isOrganizationLoading } =
    useQueryGetOrganization(
      {
        orgId: orgId!
      },
      {
        enabled: Boolean(orgId)
      }
    )

  const {
    data: organizationSettings,
    isLoading: isLoadingOrganizationSettings
  } = useQueryGetOrganizationSettings(orgId!, {
    enabled: Boolean(orgId)
  })

  const {
    organizationTags: allTags,
    isOrganizationTagsLoading: isAllTagsLoading
  } = useTags()

  const {
    isLoading: isLoadingPricingTiers,
    pricingTierAssignments,
    pricingTiers
  } = usePricingTiers()

  const { userData } = useAuth()

  const { dataPoints, isLoading: isDataPointsLoading } = useDataPoints()

  const [pricingTierState, setPricingTierState] =
    useState<PricingTierComponents.Schemas.PricingTier>()

  const [selectedTags, setSelectedTags] = useState<string[]>(
    organization?.tags ?? []
  )
  const [selectedProducts, setSelectedProducts] = useState<string[]>(
    organization?.active_products ?? []
  )
  const [selectedPricingTierId, setSelectedPricingTierId] = useState<
    string | undefined
  >()

  const [organizationState, setOrganizationState] = useState<IOrganization>(
    {} as IOrganization
  )

  const [maxBillableUsersLastMonth, setMaxBillableUsersLastMonth] =
    useState<number>(0)

  const [additionalPaidUsers, setAdditionalPaidUsers] = useState<number>(0)
  const [isUpdatingOrganization, setIsUpdatingOrganization] = useState(false)

  const dataPoint = dataPoints.find((x) => x.org_id.toString() === orgId)

  useEffect(() => {
    if (organization) {
      setSelectedProducts(organization?.active_products ?? [])
      setSelectedTags(organization?.tags ?? [])
      setOrganizationState(organization)
    }
  }, [organization])

  const { data: assignedPricingTier, isLoading: isLoadingAssignedPricingTier } =
    useQueryGetPricingTierByOrganizationId(selectedPricingTierId!, orgId!, {
      enabled: Boolean(selectedPricingTierId) && Boolean(orgId)
    })

  useEffect(() => {
    if (assignedPricingTier) {
      handleResetPricingTierState(assignedPricingTier)
    }
  }, [assignedPricingTier])

  const handleResetPricingTierState = (
    pricingTier: PricingTierComponents.Schemas.PricingTier
  ) => {
    const pricingTierDefaultValues = Object.fromEntries(
      PricingTierSettings.map((field) => [
        field.key,
        pricingTier?.settings?.[field.key] ?? field.default
      ]).filter((x) => x[0] !== 'name')
    )

    setPricingTierState({
      ...pricingTier,
      settings: pricingTierDefaultValues
    })
  }

  useEffect(() => {
    if (pricingTierAssignments) {
      const pricingTierId = pricingTierAssignments.find(
        (x) => x.organization_id === orgId
      )?.pricing_tier_id

      setSelectedPricingTierId(pricingTierId)
    }
  }, [orgId, pricingTierAssignments])

  useEffect(() => {
    if (pricingTierState && !isDataPointsLoading) {
      setMaxBillableUsersLastMonth(
        dataPoint?.non_billable_users_last_month! > 0
          ? dataPoint?.max_users_last_month! -
              dataPoint?.non_billable_users_last_month!
          : dataPoint?.max_users_last_month!
      )
      const freeUserLimit = pricingTierState?.override_settings?.free_user
        ? pricingTierState?.override_settings?.free_user?.quota
        : pricingTierState?.settings?.free_user?.quota || 0

      if (+freeUserLimit > 0 && maxBillableUsersLastMonth - freeUserLimit > 0) {
        setAdditionalPaidUsers(maxBillableUsersLastMonth - freeUserLimit)
      }
    }
  }, [
    dataPoint?.max_users_last_month,
    dataPoint?.non_billable_users_last_month,
    isDataPointsLoading,
    maxBillableUsersLastMonth,
    pricingTierState
  ])

  const { data: activePartners, isLoading: isActivePartnersLoading } =
    useQueryGetActivePartnerByOrgId(orgId!, {
      enabled: Boolean(orgId)
    })

  const { data: endCustomerPortalUsers } = useQueryUsersByOrgId(
    orgId!,
    'END_CUSTOMER_PORTAL',
    {
      enabled: Boolean(orgId)
    }
  )

  const { data: installerPortalUsers } = useQueryUsersByOrgId(
    orgId!,
    'INSTALLER_PORTAL',
    {
      enabled: Boolean(orgId)
    }
  )

  const getActualPartnerUsers = () => {
    let actualPartnerUsers = 0

    if (!isDataPointsLoading && !isActivePartnersLoading) {
      for (const activePartner of activePartners?.data.results ?? []) {
        if (activePartner?.partner_org_id) {
          actualPartnerUsers +=
            dataPoints?.find(
              (dp) => dp.org_id.toString() === activePartner?.partner_org_id
            )?.actual_users ?? 0
        }
      }
    }

    return actualPartnerUsers
  }

  const getMaxPartnerUsers = () => {
    let maxPartnerUsers = 0

    if (!isDataPointsLoading) {
      for (const activePartner of activePartners?.data.results ?? []) {
        if (activePartner?.partner_org_id) {
          maxPartnerUsers +=
            dataPoints?.find(
              (dp) => dp.org_id.toString() === activePartner?.partner_org_id
            )?.max_users_last_month ?? 0
        }
      }
    }

    return maxPartnerUsers
  }

  const { data: organizationRoles } = useQueryGetAllRoles(
    { orgId: orgId! },
    {
      enabled: Boolean(orgId)
    }
  )

  const rootRole = organizationRoles?.data.roles?.find((x) => x.slug === 'root')

  const updateOrganization = useMutationUpdateOrganization()
  const updateOrganizationPricingTier = useMutationUpdatePricingTier(orgId)
  const assignPricingTier = useMutationAssignPricingTier()
  const updateRole = useMutationUpdateRole(rootRole?.id ?? '', orgId!)

  const handleUpdateOrganization = () => {
    setIsUpdatingOrganization(true)
    if (pricingTierState?.id) {
      assignPricingTier.mutate(
        {
          organization_id: orgId,
          pricing_tier_id: pricingTierState?.id
        },
        {
          onError: (err: any) => {
            toast({
              title: 'Error',
              description: `Error while assigning pricing tier. ${err?.['message']}`
            })
          }
        }
      )

      updateOrganizationPricingTier.mutate(
        { ...pricingTierState, updated_by: userData?.attributes?.email || '' },
        {
          onError: (err: any) => {
            toast({
              title: 'Error',
              description: `Error while updating pricing tier. ${err?.['message']}`
            })
          }
        }
      )
    }

    updateRole.mutate({
      grants: getGrantsForPricingTier(pricingTierState!),
      name: rootRole!.name,
      slug: rootRole!.slug!,
      id: rootRole!.id!,
      organization_id: rootRole!.organization_id!,
      type: rootRole!.type!
    })
    updateOrganization.mutate(
      {
        ...organizationState,
        tags: selectedTags,
        active_products: selectedProducts,
        company_size: organizationState.company_size,
        pricing_tier_id: pricingTierState?.id
      },
      {
        onSuccess: () => {
          setIsUpdatingOrganization(false)
          toast({
            title: 'Success',
            description: 'Organization updated successfully'
          })
        },
        onError: (err: any) => {
          setIsUpdatingOrganization(false)
          toast({
            title: 'Error',
            description: `Error while updating organization. ${err?.['message']}`
          })
        }
      }
    )
  }

  return (
    <Tabs className="w-full" defaultValue="settings">
      <TabsList className="grid w-full grid-cols-4">
        <TabsTrigger value="settings">Settings</TabsTrigger>
        <TabsTrigger value="users">Users</TabsTrigger>
        <TabsTrigger value="roles">Roles</TabsTrigger>
        <TabsTrigger value="partners">Active Partners</TabsTrigger>
      </TabsList>
      <TabsContent value="settings">
        <div className="space-y-4">
          <div className="flex space-x-12">
            <div className="w-1/2 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="id">ID</Label>
                <Input disabled id="id" value={organization?.id} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  onChange={(e) =>
                    setOrganizationState((prev) => ({
                      ...prev,
                      name: e.target.value
                    }))
                  }
                  value={organizationState?.name ?? ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer_number">Customer Number</Label>
                <Input
                  id="customer_number"
                  onChange={(e) =>
                    setOrganizationState((prev) => ({
                      ...prev,
                      customer_number: e.target.value
                    }))
                  }
                  value={organizationState?.customer_number ?? ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="name"
                  onChange={(e) =>
                    setOrganizationState((prev) => ({
                      ...prev,
                      email: e.target.value
                    }))
                  }
                  type="email"
                  value={organizationState?.email ?? ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="symbol">Symbol</Label>
                <Input
                  disabled
                  id="symbol"
                  value={organization?.symbol ?? ''}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="type">Type</Label>
                <Input disabled id="type" value={organization?.type} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Input
                  disabled
                  id="status"
                  value={organization?.status ?? ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="created_date">Created At</Label>
                <Input
                  disabled
                  id="created_date"
                  value={
                    new Date(
                      organization?.created_date as string
                    )?.toLocaleString() ?? ''
                  }
                />
              </div>
              <Button
                loading={isUpdatingOrganization}
                onClick={() => handleUpdateOrganization()}
              >
                Update Organization
              </Button>
            </div>
            <div className="w-1/2 space-y-4">
              <div className="space-y-2">
                <Label>Tags</Label>
                <MultiSelectAutoComplete
                  allowNewValues
                  isLoading={isAllTagsLoading}
                  items={allTags || []}
                  onNewItem={(item) => item}
                  onSelect={(item) =>
                    setSelectedTags((prev) => [...prev, item as string])
                  }
                  onUnselect={(item) =>
                    setSelectedTags((prevItems) =>
                      prevItems.filter(
                        (selectedItem) => selectedItem !== (item as string)
                      )
                    )
                  }
                  placeholder="Choose the tags to assign to the organization"
                  selectedItems={selectedTags}
                />
              </div>
              <div className="space-y-2">
                <Label>Active Products</Label>
                <MultiSelectAutoComplete
                  allowNewValues
                  items={[
                    /* TODO: FETCH FROM BACKEND */
                    'PV (Solar)',
                    'Wärmepumpe (Heat Pump)',
                    'House Connection Power',
                    'House Connection Gas',
                    'House Connection Broadband',
                    'House Connection Water',
                    'House Connection District Heating',
                    'EEG Anmeldung (MPP)',
                    'Wallboxen (Charging Stations)',
                    'Heizung (Heating)',
                    'Breitband (Broadband)',
                    'Speicher (Storage)',
                    'Strom & Gas (Power & Gas)',
                    'Inbetriebsetzung (Commissioning)',
                    'Kundenportal (Customer Portal)'
                  ]}
                  onNewItem={(item) => item}
                  onSelect={(item) =>
                    setSelectedProducts([...selectedProducts, item as string])
                  }
                  onUnselect={(item) =>
                    setSelectedProducts(
                      selectedProducts.filter((x) => x !== item)
                    )
                  }
                  selectedItems={selectedProducts}
                />
              </div>
              <div className="space-y-2">
                <Label>Company Size</Label>
                <Select
                  onValueChange={(e) =>
                    setOrganizationState((prev) => ({
                      ...prev,
                      company_size: e
                    }))
                  }
                  value={organizationState.company_size}
                >
                  <SelectTrigger className="text-primary">
                    <SelectValue placeholder="Select a company size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={'Small'}>Small</SelectItem>
                    <SelectItem value={'Medium'}>Medium</SelectItem>
                    <SelectItem value={'Large'}>Large</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pricing_tier">Pricing Tier</Label>
                <Select
                  onValueChange={(pricingTierId) => {
                    setSelectedPricingTierId(pricingTierId)
                  }}
                  value={selectedPricingTierId}
                >
                  <SelectTrigger className="text-primary">
                    <SelectValue placeholder="Select a type" />
                  </SelectTrigger>

                  <SelectContent>
                    {pricingTiers?.map((pricingTier) => {
                      return (
                        <SelectItem
                          key={pricingTier.id}
                          value={pricingTier.id!}
                        >
                          {pricingTier?.name}
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
              </div>
              <PricingTierAdvancedSettings
                defaultOpen={false}
                loading={isLoadingAssignedPricingTier || isLoadingPricingTiers}
                onChange={(e, key) => {
                  setPricingTierState({
                    ...pricingTierState,
                    override_settings: {
                      ...pricingTierState?.override_settings,
                      [key]: e
                    }
                  })
                }}
                options={PricingTierSettings.filter(
                  (x) => x.key !== 'name'
                ).map((x) => ({
                  ...x,
                  value:
                    pricingTierState?.override_settings?.[x.key] ??
                    pricingTierState?.settings?.[x.key],
                  isShowGlobalSetting: pricingTierState?.override_settings?.[
                    x.key
                  ]
                    ? !isEqual(
                        pricingTierState?.settings?.[x.key],
                        pricingTierState?.override_settings?.[x.key]
                      )
                    : false,
                  globalSetting:
                    pricingTierState?.settings?.[x.key] ?? x.default
                }))}
              />
              <OrganizationDataPoints
                dataPoint={{
                  max_customer: dataPoint?.max_customer || 0,
                  actual_customer: dataPoint?.actual_customer || 0,
                  actual_users: dataPoint?.actual_users || 0,
                  max_users_last_month: dataPoint?.max_users_last_month || 0,
                  maxBillableUsersLastMonth: maxBillableUsersLastMonth || 0,
                  additionalPaidUsers: additionalPaidUsers || 0
                }}
                defaultOpen={false}
                loading={isDataPointsLoading || isOrganizationLoading}
              />
              <OrganizationPartnership
                defaultOpen={false}
                loading={isDataPointsLoading || isOrganizationLoading}
                partnershipData={{
                  actual_partner_users: getActualPartnerUsers() || 0,
                  max_partner_users: getMaxPartnerUsers() || 0,
                  active_partnerships:
                    activePartners?.data.results?.length || 0,
                  ecp_users: endCustomerPortalUsers?.data.hits || 0,
                  ip_users: installerPortalUsers?.data.hits || 0
                }}
              />
              <OrganizationSettings
                defaultOpen={false}
                loading={isLoadingOrganizationSettings}
                settings={organizationSettings?.data || {}}
              />
            </div>
          </div>
        </div>
      </TabsContent>
      <TabsContent value="users">
        <UserList organizationId={organization?.id} />
      </TabsContent>
      <TabsContent value="roles">
        <RoleList organizationId={organization?.id} />
      </TabsContent>
      <TabsContent value="partners">
        <PartnerList />
      </TabsContent>
    </Tabs>
  )
}

export default OrganizationDetail
